import {
  type GetCustomerOrdersQuery,
  type MailingAddressFragmentFragment,
  type OrderFragmentFragment,
} from '@data/shopify/storefront/types'
import { type AddressFormValues, type UserOrder } from '@lib/user'

/**
 * Parses Shopify customer order node.
 */
export const parseOrderNode = ({
  name,
  processedAt,
  financialStatus,
  fulfillmentStatus,
  totalPriceV2,
  statusUrl,
}: OrderFragmentFragment) => {
  const userOrder: UserOrder = {
    id: name,
    date: processedAt,
    paymentStatus: financialStatus ?? null,
    fulfillmentStatus: fulfillmentStatus,
    total: Number(totalPriceV2.amount),
    url: statusUrl,
  }

  return userOrder
}

/**
 * Gets user orders from Shopify order query result.
 */
export const parseCustomerOrders = (
  customerOrdersQuery?: GetCustomerOrdersQuery,
) => {
  const orderNodes = (customerOrdersQuery?.customer?.orders?.edges
    ?.map((edge) => edge.node)
    ?.filter(Boolean) ?? []) as OrderFragmentFragment[]

  return orderNodes
    .sort(
      (orderNode1, orderNode2) =>
        Date.parse(orderNode2.processedAt) - Date.parse(orderNode1.processedAt),
    )
    .map(parseOrderNode)
}

/**
 * Parses Shopify mailing address into address form values.
 */
export const parseMailingAddressValues = (
  address: MailingAddressFragmentFragment,
  defaultAddressId?: string,
) => {
  const addressFormValues: AddressFormValues = {
    firstName: address.firstName ?? '',
    lastName: address.lastName ?? '',
    company: address.company ?? '',
    address1: address.address1 ?? '',
    address2: address.address2 ?? '',
    city: address.city ?? '',
    country: address.country ?? '',
    zip: address.zip ?? '',
    phone: address.phone ?? '',
    isDefault: address.id === defaultAddressId,
  }

  return addressFormValues
}

/**
 * Gets user order cursor from Shopify user query results.
 */
export const getCustomerOrderCursor = (
  customerOrdersQuery?: GetCustomerOrdersQuery,
) => {
  return customerOrdersQuery?.customer?.orders?.edges
    ?.map((edge) => edge.cursor)
    ?.pop()
}

/**
 * Gets whether query has more orders from Shopify user query results.
 */
export const getCustomerHasMoreOrders = (
  customerOrdersQuery?: GetCustomerOrdersQuery,
) => {
  return customerOrdersQuery?.customer?.orders.pageInfo?.hasNextPage
}
